import { supabase } from './auth';
import { WorkoutRecommendation } from '../types';
import { localDate } from '../utils/date';

export interface StoredRecommendation {
  day: string;
  source: 'ai' | 'rules';
  metrics_source: string | null;
  recommendation: WorkoutRecommendation;
  coach_intro: string | null;
  rating: 'up' | 'down' | null;
  completed: boolean | null;
}

const RECOMMENDATION_COLUMNS =
  'day, source, metrics_source, recommendation, coach_intro, rating, completed';

const today = () => localDate();

export const getTodaysRecommendation = async (): Promise<StoredRecommendation | null> => {
  const { data, error } = await supabase
    .from('daily_recommendations')
    .select(RECOMMENDATION_COLUMNS)
    .eq('day', today())
    .maybeSingle();

  if (error) throw error;
  return (data as StoredRecommendation | null) ?? null;
};

/** Past plans, newest first (today included). */
export const listRecentRecommendations = async (
  days = 14
): Promise<StoredRecommendation[]> => {
  const since = localDate(-days);
  const { data, error } = await supabase
    .from('daily_recommendations')
    .select(RECOMMENDATION_COLUMNS)
    .gte('day', since)
    .order('day', { ascending: false });

  if (error) throw error;
  return (data ?? []) as StoredRecommendation[];
};

/** Thumbs up/down on a day's plan (null clears it). */
export const rateRecommendation = async (
  day: string,
  rating: 'up' | 'down' | null
): Promise<void> => {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return;

  const { error } = await supabase
    .from('daily_recommendations')
    .update({ rating, updated_at: new Date().toISOString() })
    .eq('user_id', user.id)
    .eq('day', day);
  if (error) throw error;
};

/** "Did you do it?" for a day's plan (null clears the answer). */
export const markRecommendationCompleted = async (
  day: string,
  completed: boolean | null
): Promise<void> => {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return;

  const { error } = await supabase
    .from('daily_recommendations')
    .update({ completed, updated_at: new Date().toISOString() })
    .eq('user_id', user.id)
    .eq('day', day);
  if (error) throw error;
};

export const saveTodaysRecommendation = async (entry: {
  source: 'ai' | 'rules';
  metricsSource: string;
  recommendation: WorkoutRecommendation;
  coachIntro: string | null;
}): Promise<void> => {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return;

  const { error } = await supabase.from('daily_recommendations').upsert(
    {
      user_id: user.id,
      day: today(),
      source: entry.source,
      metrics_source: entry.metricsSource,
      recommendation: entry.recommendation,
      coach_intro: entry.coachIntro,
      updated_at: new Date().toISOString(),
    },
    { onConflict: 'user_id,day' }
  );
  if (error) throw error;
};

/** Persist a chat-adjusted plan so it survives refreshes. */
export const updateTodaysRecommendation = async (
  recommendation: WorkoutRecommendation
): Promise<void> => {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return;

  const { error } = await supabase
    .from('daily_recommendations')
    .update({ recommendation, updated_at: new Date().toISOString() })
    .eq('user_id', user.id)
    .eq('day', today());
  if (error) throw error;
};
